import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
} from '@chakra-ui/react';
import { getRegistryAdmin, updateRegistry } from '../services/RegistryService';
import { sendRegistryActivationNotifications } from '../services/RegistryService';
import MealScheduleCustomizationDialog from '../components/MealScheduleCustomizationDialog';

// Helper function to format date
const formatDate = (dateString: string): string => {
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayOfWeek = date.getUTCDay();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const shortYear = year.toString().slice(2, 4);
  return `${dayNames[dayOfWeek]} ${month}/${day}/${shortYear}`;
};

const today = (() => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
})();

const oneYearFromToday = (() => {
  const now = new Date();
  now.setFullYear(now.getFullYear());
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
})();

const Activate = () => {
  const { registryId, activationId } = useParams();
  const [registry, setRegistry] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [firstDeliveryDate, setFirstDeliveryDate] = useState('');
  const [isDateValid, setIsDateValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);
  const [customMealSchedule, setCustomMealSchedule] = useState<Array<{
    date: string;
    timeWindow: { start: string; end: string };
  }> | null>(null);
  const navigate = useNavigate();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    const fetchRegistry = async () => {
      try {
        if (!registryId || !activationId) {
          setError('Missing registry or activation information');
          setLoading(false);
          return;
        }
        const registryData = await getRegistryAdmin(registryId);
        if (!registryData) {
          setError('Registry not found');
          setLoading(false);
          return;
        }
        setRegistry(registryData);
        if (registryData.activationId === activationId) {
          setIsAuthorized(true);
          setFirstDeliveryDate(registryData.firstDeliveryDate || '');
        } else {
          setIsAuthorized(false);
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load registry information');
        setLoading(false);
      }
    };
    fetchRegistry();
  }, [registryId, activationId]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setFirstDeliveryDate(inputValue);

    if (!inputValue) {
      setIsDateValid(false);
      return;
    }
    const [yearStr, monthStr, dayStr] = inputValue.split('-').map(num => parseInt(num, 10));
    if (!yearStr || !monthStr || !dayStr || isNaN(yearStr) || isNaN(monthStr) || isNaN(dayStr)) {
      setIsDateValid(false);
      return;
    }
    const selectedDate = new Date(Date.UTC(yearStr, monthStr - 1, dayStr, 12, 0, 0));
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneYearFromTodayDate = new Date(todayDate);
    oneYearFromTodayDate.setFullYear(oneYearFromTodayDate.getFullYear() + 1);
    const dateIsLessThan1YearFromToday = selectedDate <= oneYearFromTodayDate;
    setIsDateValid(selectedDate >= todayDate && dateIsLessThan1YearFromToday);
  };

  // Format time for display
  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Generate default meal dates
  const getDefaultMealDates = () => {
    if (!firstDeliveryDate || !registry.mealDays) return [];
    
    const dates = [];
    const dateParts = firstDeliveryDate.split('-').map(Number);
    
    for (let i = 0; i < registry.mealDays; i++) {
      const date = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2]));
      date.setUTCDate(date.getUTCDate() + i);
      const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
      dates.push(dateStr);
    }
    
    return dates;
  };

  // Get current meal schedule (custom or default)
  const getCurrentMealSchedule = () => {
    if (customMealSchedule) {
      return customMealSchedule;
    }
    
    const defaultDates = getDefaultMealDates();
    return defaultDates.map(date => ({
      date,
      timeWindow: registry.defaultTimeWindow || { start: '17:00', end: '19:00' }
    }));
  };

  const handleCustomizeSchedule = () => {
    setIsCustomizationModalOpen(true);
  };

  const handleSaveCustomSchedule = (customDates: Array<{ date: string; timeWindow: { start: string; end: string } }>) => {
    setCustomMealSchedule(customDates);
    setIsCustomizationModalOpen(false);
  };

  const handleActivate = async () => {
    if (!registryId || !firstDeliveryDate) return;
    setIsSubmitting(true);
    try {
      const currentRegistry = await getRegistryAdmin(registryId);
    
      if (!currentRegistry) {
        setError('Registry not found. Please refresh and try again.');
        return;
      }
      
      // Check if the registry has already been activated by someone else
      if (currentRegistry.status === 'ACTIVE') {
        setError('This MommaFuel has already been activated by someone else.');
        setTimeout(() => {
          navigate(`/registry/${registryId}`);
        }, 2000);
        return;
      }
      
      registry.status = 'ACTIVE';
      registry.firstDeliveryDate = firstDeliveryDate;
      
      // Pass custom meal schedule for activation (temporary field)
      registry.customMealSchedule = customMealSchedule;
      
      await updateRegistry(registryId, registry);
      if (process.env.REACT_APP_SEND_EMAILS) {
        sendRegistryActivationNotifications(registryId);
      }
      setSuccessMessage('MommaFuel activated! Members can now sign up to bring meals.');
      setTimeout(() => {
        navigate(`/registry/${registryId}`);
      }, 2000);
    } catch (err) {
      setError('Failed to activate MommaFuel');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={10} centerContent>
        <Spinner size="xl" color="mamaFuel.moss.500" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
        <Box mt={4}>
          <Link to="/">
            <Button colorScheme="blue">Return Home</Button>
          </Link>
        </Box>
      </Container>
    );
  }

  if (!isAuthorized) {
    return (
      <Container maxW="container.md" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Unauthorized: The activation link is invalid or expired.
        </Alert>
        <Box mt={4}>
          <Link to="/">
            <Button colorScheme="blue">Return Home</Button>
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.sm" py={10}>
      <Box
        bg={bgColor}
        p={6}
        borderRadius="md"
        boxShadow="md"
        border="1px"
        borderColor={borderColor}
      >
        <Heading size="lg" mb={4} color="mamaFuel.moss.700">
          Activate this MommaFuel
        </Heading>
        <VStack align="stretch" spacing={4}>
          <Text>
            To activate <strong>{registry.parentNames}</strong>'s MommaFuel, please select the first meal date.
          </Text>
          <FormControl isRequired>
            <FormLabel fontWeight="bold">First Meal Date</FormLabel>
            <Input
              type="date"
              value={firstDeliveryDate}
              max="2099-12-31"
              onChange={handleDateChange}
            />
          </FormControl>

          {/* Activation Confirmation Section */}
          {firstDeliveryDate && firstDeliveryDate >= today && (
          <Box
            p={6}
            borderRadius="md"
            borderWidth="1px"
            bg="blue.50"
            mt={4}
          >
            <Text mb={2}>
              You are about to activate this MommaFuel which means:
            </Text>
            <VStack align="start" spacing={2} mb={4} pl={4}>
              <Text>• Members will be notified meal signups are now open</Text>
              <Text>• Address can no longer be edited</Text>
              <Text>• This action cannot be undone</Text>
            </VStack>
            <Box
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="white"
              mb={6}
            >
              
              <Heading size="sm">Meal Schedule</Heading>
              <Text mb={2}>The following dates will be available for meal signup:</Text>
              <VStack align="start" spacing={1} pl={2}>
                {getCurrentMealSchedule().map((meal, index) => (
                  <Flex 
                    key={index}
                    direction={{ base: "column", md: "row" }}
                    justify={{ base: "flex-start", md: "space-between" }}
                    align={{ base: "flex-start", md: "center" }}
                    width="100%"
                    gap={{ base: 1, md: 0 }}
                  >
                    <Text>
                      <strong>Day {index + 1}:</strong> {formatDate(meal.date)}
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      ({formatTime(meal.timeWindow.start)} - {formatTime(meal.timeWindow.end)})
                    </Text>
                  </Flex> 
                ))}
              </VStack>
              <Button
                  size="sm"
                  mt={2}
                  colorScheme="blue"
                  variant="outline"
                  onClick={handleCustomizeSchedule}
                >
                  Customize Dates & Times
                </Button>
              
            </Box>
            <Text fontWeight="bold">
              Are you sure you want to proceed?
            </Text>
          </Box>
          )}
        <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        gap={4} // adds spacing between buttons
        >
        <Button
            variant="outline"
            as={Link}
            to={`/registry/${registryId}`}
            width={{ base: "100%", md: "auto" }}
        >
            View MommaFuel
        </Button>
        <Button
            colorScheme="green"
            onClick={handleActivate}
            isLoading={isSubmitting}
            isDisabled={!firstDeliveryDate || !isDateValid}
            width={{ base: "100%", md: "auto" }}
        >
            Yes, Open for Meal Signups
        </Button>
        </Flex>
          {successMessage && (
            <Alert status="success" borderRadius="md" mt={4}>
              <AlertIcon />
              {successMessage}
            </Alert>
          )}
        </VStack>
      </Box>

      {/* Meal Schedule Customization Modal */}
      <MealScheduleCustomizationDialog
        isOpen={isCustomizationModalOpen}
        onClose={() => setIsCustomizationModalOpen(false)}
        onSave={handleSaveCustomSchedule}
        defaultDates={getDefaultMealDates()}
        defaultTimeWindow={registry?.defaultTimeWindow || { start: '17:00', end: '19:00' }}
        currentCustomSchedule={customMealSchedule}
      />
    </Container>
  );
};

export default Activate;